import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { useRef } from "react";
import { api, API } from "@/lib/api";
import { CartProvider } from "@/context/CartContext";
import Header from "@/components/Header";
import BottomNav from "@/components/BottomNav";
import CartDrawer from "@/components/CartDrawer";
import PwaInstall from "@/components/PwaInstall";
import Home from "@/pages/Home";
import Catalogue from "@/pages/Catalogue";
import Profile from "@/pages/Profile";
import Admin from "@/pages/Admin";
import Reserve from "@/pages/Reserve";

// Suivi des sessions issues d'un QR code : mémorise le jeton (?qs=…), enregistre
// chaque page vue et signale la présence toutes les 20 s (durée réelle de visite).
function QrTracker() {
  const location = useLocation();
  const lastPath = useRef(null);

  useEffect(() => {
    const qs = new URLSearchParams(location.search).get("qs");
    if (qs) localStorage.setItem("qr_session", qs);
  }, [location.search]);

  useEffect(() => {
    const token = localStorage.getItem("qr_session");
    if (!token) return;
    const path = location.pathname + location.search;
    if (path === lastPath.current) return;
    lastPath.current = path;
    api.post("/qr/track", { token, path }).catch(() => {});
  }, [location]);

  useEffect(() => {
    const beat = () => {
      const token = localStorage.getItem("qr_session");
      if (!token) return;
      const payload = JSON.stringify({ token, path: location.pathname });
      if (navigator.sendBeacon) {
        navigator.sendBeacon(`${API}/qr/ping`, new Blob([payload], { type: "application/json" }));
      } else {
        api.post("/qr/ping", { token, path: location.pathname }).catch(() => {});
      }
    };
    const iv = setInterval(() => {
      if (document.visibilityState === "visible") beat();
    }, 20000);
    window.addEventListener("pagehide", beat);
    return () => {
      clearInterval(iv);
      window.removeEventListener("pagehide", beat);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}

let lenisInstance = null;

// Remonte en haut de page à chaque navigation (sinon la nouvelle page garde
// la position de défilement de la précédente et semble « plaquée vers le bas »)
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    lenisInstance?.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [pathname]);
  return null;
}

function App() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    lenisInstance = lenis;
    let frame;
    const raf = (time) => {
      lenis.raf(time);
      frame = requestAnimationFrame(raf);
    };
    frame = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(frame);
      lenis.destroy();
    };
  }, []);

  return (
    <div className="App">
      <AuthProvider>
        <CartProvider>
          <BrowserRouter>
            <ScrollToTop />
            <QrTracker />
            <Header />
            <main className="pb-[calc(8rem+env(safe-area-inset-bottom,0px))]">
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/vins" element={<Catalogue />} />
                <Route path="/profil" element={<Profile />} />
                <Route path="/reserver" element={<Reserve />} />
                <Route path="/admin" element={<Admin />} />
              </Routes>
            </main>
            <BottomNav />
            <CartDrawer />
            <PwaInstall />
            <Toaster position="top-center" richColors offset={{ top: "calc(env(safe-area-inset-top, 0px) + 84px)" }} mobileOffset={{ top: "calc(env(safe-area-inset-top, 0px) + 84px)" }} />
          </BrowserRouter>
        </CartProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
